import React, { useState } from 'react';
import './TaskChecklist.css';

function formatDate(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return ''; }
}

function TaskChecklist({ tasks = [], onAdd, onUpdate, onDelete, canManage, users = [] }) {
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newAssigned, setNewAssigned] = useState('');
  const [newDue, setNewDue] = useState('');

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!newTitle.trim()) return;
    const taskData = { title: newTitle.trim() };
    if (newAssigned) taskData.assigned_to = newAssigned;
    if (newDue) taskData.due_date = newDue;
    onAdd(taskData);
    setNewTitle('');
    setNewAssigned('');
    setNewDue('');
    setAdding(false);
  };

  const toggleStatus = (task) => {
    const nextStatus = task.status === 'done' ? 'pending' : 'done';
    onUpdate(task.id, { status: nextStatus });
  };

  const displayName = (username) => {
    if (!username) return '';
    const user = users.find(u => u.username === username);
    if (user && user.display_name && user.display_name.trim()) return user.display_name.trim();
    return username;
  };

  const done = tasks.filter(t => t.status === 'done').length;
  const total = tasks.length;

  return (
    <div className="task-checklist" onClick={(e) => e.stopPropagation()}>
      <div className="task-checklist-header">
        <span className="task-checklist-title">Tasks</span>
        {total > 0 && (
          <span className="task-checklist-progress">
            {done}/{total}
            <span className="task-progress-bar">
              <span className="task-progress-fill" style={{ width: `${(done / total) * 100}%` }} />
            </span>
          </span>
        )}
        {canManage && !adding && (
          <button className="task-add-btn" onClick={(e) => { e.stopPropagation(); setAdding(true); }} type="button">+ Add</button>
        )}
      </div>

      {tasks.length > 0 && (
        <ul className="task-list">
          {tasks.map(task => (
            <li key={task.id} className={`task-item ${task.status === 'done' ? 'task-done' : ''}`}>
              <button
                className="task-checkbox"
                onClick={(e) => { e.stopPropagation(); toggleStatus(task); }}
                disabled={!canManage}
                type="button"
                aria-label={task.status === 'done' ? 'Mark incomplete' : 'Mark complete'}
              >
                {task.status === 'done' ? (
                  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                ) : (
                  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" width="16" height="16"><rect x="3" y="3" width="14" height="14" rx="3"/></svg>
                )}
              </button>
              <div className="task-content">
                <span className="task-title-text">{task.title}</span>
                <div className="task-meta">
                  {task.assigned_to && <span className="task-owner">{displayName(task.assigned_to)}</span>}
                  {task.due_date && <span className="task-due">{formatDate(task.due_date)}</span>}
                </div>
              </div>
              {canManage && (
                <button
                  className="task-delete-btn"
                  onClick={(e) => { e.stopPropagation(); onDelete(task.id); }}
                  type="button"
                  aria-label="Delete task"
                >×</button>
              )}
            </li>
          ))}
        </ul>
      )}

      {adding && (
        <form className="task-add-form" onSubmit={handleAdd} onClick={(e) => e.stopPropagation()}>
          <input
            className="task-input"
            placeholder="Task title..."
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            autoFocus
          />
          <select
            className="task-input task-input-sm"
            value={newAssigned}
            onChange={(e) => setNewAssigned(e.target.value)}
          >
            <option value="">Assigned to</option>
            {users.map(u => (
              <option key={u.username} value={u.username}>
                {u.display_name && u.display_name.trim() ? u.display_name.trim() : u.username}
              </option>
            ))}
          </select>
          <input
            className="task-input task-input-sm"
            type="date"
            value={newDue}
            onChange={(e) => setNewDue(e.target.value)}
          />
          <div className="task-add-actions">
            <button type="submit" className="task-save-btn">Add</button>
            <button type="button" className="task-cancel-btn" onClick={(e) => { e.stopPropagation(); setAdding(false); }}>Cancel</button>
          </div>
        </form>
      )}

      {tasks.length === 0 && !adding && (
        <div className="task-empty">No tasks yet{canManage ? ' — click "+ Add" to create one' : ''}</div>
      )}
    </div>
  );
}

export default TaskChecklist;
